import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { AggregationsResults } from '@core/Entities/Search/Aggregations/Aggregations.entity';
import type { PlpRoot } from '@core/Entities/Search/PlpRoot.entity';
import type { UrlParams } from '@core/Entities/Search/QueryParams.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { AGGREGATION_FILTERS } from '@core/Services/Search/Filters.constants';
import { mapQueryParams } from '@core/Services/Search/Utils/mapQueryParams';
import { fetchJson } from '@core/Utils/Fetch/FetchJson';
import { aggregationsToMakesModels } from '@core/Utils/Search/AggregationsToMakesModels';
import { getPageFiltersFromRoot } from '@core/Utils/Search/GetPageFiltersFromRoot';
import { getSearchUrl } from '@core/Utils/Search/GetSearchUrl';

export const fetchAggregationsWithActiveFilters = async (
  activeFilters: Filter[],
  root: PlpRoot,
  revalidate?: number,
  tenant?: Tenant,
) => {
  const rootFilters = getPageFiltersFromRoot(root);
  const filtersAsUrlParams = () => {
    const urlParams: UrlParams = {};
    activeFilters.forEach(fltr => {
      urlParams[fltr.id] = fltr.value;
    });
    return urlParams;
  };

  const combinedParams = {
    ...filtersAsUrlParams(),
    ...rootFilters,
  };

  const URL = getSearchUrl();

  const queryParams = `&${mapQueryParams(combinedParams, tenant).queryString}`;

  const aggsToFetch = queryParams.includes('model=')
    ? [...AGGREGATION_FILTERS, 'variant']
    : AGGREGATION_FILTERS;

  const data = await fetchJson(`${URL}/count?field=${aggsToFetch.join('&field=')}${queryParams}`, {
    headers: {
      'x-heycar-tenant': 'uk',
    },
    ...(revalidate !== undefined ? { next: { revalidate } } : {}),
  });
  if (!data) {
    throw new Error();
  }

  const { aggregations, total: totalCount } = data as AggregationsResults;

  const { makes, models, variants } = aggregationsToMakesModels(
    aggregations.make!,
    aggregations.model!,
    aggregations.variant,
  );

  return {
    'stock-condition': aggregations['stock-condition'],
    price: aggregations.price,
    'monthly-price': aggregations['monthly-price'],
    makes,
    models,
    variants,
    'fuel-type': aggregations['fuel-type'],
    'body-type': aggregations['body-type'],
    color: aggregations.color,
    'gear-box': aggregations['gear-box'],
    doors: aggregations.doors,
    seats: aggregations.seats,
    totalCount,
    'finance-product': aggregations['finance-product'],
    'engine-size': aggregations['engine-size'],
  };
};
